import { Skeleton, SkeletonText } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { QueryError } from 'common/components/QueryError';
import { QuerySuspense } from 'common/components/QuerySuspense';
import { ErrorBoundary } from 'common/components/withErrorBoundary';
import { useFeature } from 'common/hooks/useFeature';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useUser } from 'modules/app/hooks/useUser';
import { CardTransactionDetailsPanel } from 'modules/cards/components/CardTransactionDetailsPanel/CardTransactionDetailsPanel';
import { useInvalidateCardAggregateActivityQuery } from 'modules/cards/hooks/useCardAggregateActivity';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { SessionStorageKey } from 'src/core/constants/storage';
import { getIsFailedTransactionsEnabled } from 'src/core/selectors/globalSelectors';
import { setSessionItem as setItem } from 'src/core/utils/storage';

import { CardActivation } from './CardActivation/CardActivation';
import { PlasticCard } from './PlasticCard';
import type { ApiCard } from '../../card';
import { useCardActivityQuery } from '../../hooks/useCardActivityQuery';
import { useCardLoadsQuery } from '../../hooks/useCardLoadsQuery';
import { useCardPaymentsQuery } from '../../hooks/useCardPaymentsQuery';
import { useCardQuery } from '../../hooks/useCardQuery';
import { useCardSettingsQuery } from '../../hooks/useCardSettingsQuery';
import { usePlasticCard } from '../hooks/usePlasticCard';

type Props = {
  cardId: string;
};

const CardContainer = ({ cardId }: Props) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const companyId = useCompanyId();
  const [isCardActivationStep1Open, setIsCardActivationStep1Open] =
    useState(false);

  const isFailedTransactionsEnabled = useSelector(
    getIsFailedTransactionsEnabled,
  );

  const cardQueryState = useCardQuery(cardId);
  const cardActivityQueryState = useCardActivityQuery(cardId, {
    isEnabled: !isFailedTransactionsEnabled,
  });
  const cardLoadsQueryState = useCardLoadsQuery(cardId, {
    isEnabled: !isFailedTransactionsEnabled,
  });
  const cardPaymentsQueryState = useCardPaymentsQuery(cardId, {
    isEnabled: !isFailedTransactionsEnabled,
  });

  const cardSettingsQueryState = useCardSettingsQuery(cardId);

  const invalidateCardAggregateActivityQuery =
    useInvalidateCardAggregateActivityQuery();

  const cardTransactionDetailsMatch = useRouteMatch(
    routes.CARD_TRANSACTION_DETAILS.path,
  );
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | undefined
  >(cardTransactionDetailsMatch?.params?.transactionId);

  useEffect(() => {
    const { transactionId } = cardTransactionDetailsMatch?.params ?? {};
    setSelectedTransactionId(transactionId);
  }, [cardTransactionDetailsMatch]);

  const closeSidePanel = () => {
    setSelectedTransactionId(undefined);
    history.push(routeFor(routes.CARD.path, { company: companyId }));
  };

  return (
    <QuerySuspense
      queryState={cardQueryState}
      loading={
        <div className="page__container">
          <div className="rounded-12 bg-background-secondary-default mx-auto my-48 flex w-[980px] gap-32 p-48">
            <Skeleton width="300px" height="190px" />
            <div className="flex flex-col">
              <SkeletonText className="mb-8" size="xxl" width="140px" />
              <SkeletonText className="mb-16" width="140px" />
              <SkeletonText className="mb-8" size="xxl" width="140px" />
              <SkeletonText className="mb-16" width="140px" />
            </div>
          </div>
        </div>
      }
      fallback={(error) => (
        <QueryError componentType="Callout" queryError={error} />
      )}
    >
      {(card) => {
        if (
          (!getIsCardActivated(card) && card.banking_provider === 'marqeta') ||
          isCardActivationStep1Open
        ) {
          return (
            <div className="page__container">
              <CardActivation
                card={card}
                onFinish={async () => {
                  setItem(SessionStorageKey.CardActivated, '1');
                  await queryClient.invalidateQueries(['cards', cardId], {
                    exact: true,
                  });

                  window.location.reload();
                }}
                startStep={isCardActivationStep1Open ? 1 : 0}
              />
            </div>
          );
        }

        return (
          <>
            <div className="page__container">
              <PlasticCard
                card={card}
                cardActivity={unwrapQuery(cardActivityQueryState)}
                cardLoads={unwrapQuery(cardLoadsQueryState)}
                cardPayments={unwrapQuery(cardPaymentsQueryState)}
                cardSettings={unwrapQuery(cardSettingsQueryState)}
                selectedTransactionId={selectedTransactionId}
                refreshCard={async () => {
                  await queryClient.invalidateQueries(['cards', cardId], {
                    exact: true,
                  });
                  await queryClient.invalidateQueries([
                    'cards',
                    cardId,
                    'activity',
                  ]);
                  await queryClient.invalidateQueries([
                    'cards',
                    cardId,
                    'loads',
                  ]);
                  await invalidateCardAggregateActivityQuery(cardId);
                }}
                onCardReceptionConfirmationClick={() =>
                  setIsCardActivationStep1Open(true)
                }
              />
            </div>
            <ErrorBoundary
              context={{
                team: 'cards-ux',
                scope: 'card::panel',
              }}
            >
              {selectedTransactionId && (
                <CardTransactionDetailsPanel
                  companyId={companyId}
                  onClose={closeSidePanel}
                  transactionId={selectedTransactionId}
                />
              )}
            </ErrorBoundary>
          </>
        );
      }}
    </QuerySuspense>
  );
};

export const MyCardContainer = () => {
  const company = useCompany();
  const history = useHistory();
  const isPageAccessible = useIsPageAccessible();
  const card = usePlasticCard();

  useEffect(() => {
    if (!isPageAccessible || !card?.id) {
      history.push(routeFor(routes.NOT_FOUND.path, { company: company.id }));
    }
  }, [isPageAccessible]);

  if (!isPageAccessible || !card?.id) {
    return null;
  }

  return (
    <ErrorBoundary context={{ scope: 'my-card', team: 'cards-ux' }}>
      <CardContainer cardId={card?.id} />
    </ErrorBoundary>
  );
};

const getIsCardActivated = (card: ApiCard): boolean => {
  return Boolean(card.last_pin_action) || Boolean(card.date_first_activation);
};

const useIsPageAccessible = (): boolean => {
  const user = useUser();
  const company = useCompany();
  const hasPlasticCardsFeature = useFeature(FEATURES.PLASTIC_CARDS);
  return (
    hasPlasticCardsFeature && user.has_plastic_card && !company.churning_at
  );
};
